import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { EditorContent, useEditor } from '@tiptap/react'
import { TaskItem } from '@tiptap/extension-list'
import { richTextExtensions } from './richTextExtensions'

export const useRichTextStyles = makeStyles({
    // Read-only rendering of markdown content. The ProseMirror schema (see richTextExtensions)
    // only keeps known nodes/marks, so it sanitizes the content on its own — no DOMPurify needed.
    content: {
        '& .ProseMirror': {
            outline: 'none',
        },
        '& .ProseMirror p': {
            margin: 0,
        },
        '& .ProseMirror p + p': {
            marginTop: tokens.spacingVerticalS,
        },
        '& .ProseMirror ul, & .ProseMirror ol': {
            margin: `${tokens.spacingVerticalS} 0`,
            paddingLeft: '24px',
        },
        '& .ProseMirror h1, & .ProseMirror h2, & .ProseMirror h3': {
            margin: `${tokens.spacingVerticalM} 0 ${tokens.spacingVerticalXS}`,
        },
        // Same shape as in FormikRichTextField, so a task list reads identically in the form and here.
        '& .ProseMirror ul[data-type="taskList"]': {
            listStyle: 'none',
            paddingLeft: 0,
        },
        '& .ProseMirror ul[data-type="taskList"] li': {
            display: 'flex',
            alignItems: 'flex-start',
            gap: tokens.spacingHorizontalXS,
        },
        '& .ProseMirror ul[data-type="taskList"] li > span': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            // Sized and placed like the checkbox in the edit form, so a task list does not jump when it is opened.
            width: '14px',
            height: '14px',
            marginTop: '4px',
            border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
            borderRadius: tokens.borderRadiusSmall,
            fontSize: tokens.fontSizeBase200,
            lineHeight: 1,
        },
        '& .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > span': {
            backgroundColor: tokens.colorBrandBackground,
            border: `1px solid ${tokens.colorBrandBackground}`,
            color: tokens.colorNeutralForegroundOnBrand,
        },
        '& .ProseMirror ul[data-type="taskList"] li > div': {
            flexGrow: 1,
            minWidth: 0,
        },
        // Same shape as in FormikRichTextField: a line in the margin rather than a box.
        '& .ProseMirror blockquote': {
            margin: `${tokens.spacingVerticalS} 0 ${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            paddingLeft: tokens.spacingHorizontalM,
            borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
        },
        '& .ProseMirror a': {
            color: tokens.colorBrandForegroundLink,
            textDecoration: 'underline',
        },
    },
})

/**
 * A task item that draws its checkbox as a span instead of an <input>.
 *
 * Two reasons. The box is not clickable here anyway - ticking happens in the edit form - so offering a form control
 * misleads the reader and hands the screen reader a checkbox that does nothing. And ProseMirror creates its DOM
 * outside React, which Fluent cannot cope with: `usePositioning` walks the subtree of every tooltip, popover and menu
 * surface looking for `autoFocus` props, and its `getReactFiberFromNode` throws on any input, button, select or
 * textarea without a React fiber. A task list inside a TooltippedText (as on the task board) took the whole app down
 * that way. That check only runs outside production builds, but a dev build that cannot boot is no better.
 */
const ReadOnlyTaskItem = TaskItem.extend({
    addNodeView() {
        return ({ node }) => {
            const item = document.createElement('li')
            item.dataset.type = 'taskItem'
            item.dataset.checked = String(!!node.attrs.checked)

            const checkbox = document.createElement('span')
            checkbox.setAttribute('aria-hidden', 'true')
            checkbox.textContent = node.attrs.checked ? '✓' : ''

            const content = document.createElement('div')

            item.append(checkbox, content)
            return { dom: item, contentDOM: content }
        }
    },
})

export interface RichTextProps {
    content?: string
    className?: string
    /**
     * Shows links as clickable links. Must match the `allowLinks` of the field the content was written in:
     * without the mark the reader only gets the label text of an anchor.
     */
    allowLinks?: boolean
}

/**
 * Renders markdown content read-only. tiptap-markdown parses the string into the ProseMirror
 * schema, which only keeps known nodes and marks — that is what sanitizes the content.
 *
 * `html: true` is needed because part of the content is not markdown at all: FormikRichTextField
 * stores what the editor produces, which is HTML. With `html: false` markdown-it escapes those
 * tags, so the user reads `<p>omschrijving</p>` instead of seeing a paragraph.
 */
export function RichText(props: RichTextProps): ReactElement {
    const richTextStyles = useRichTextStyles()

    const editor = useEditor({
        editable: false,
        // The same extension list as the edit form, so what is written and what is read can never drift apart.
        // The checkboxes are read-only: ReadOnlyTaskItem draws them rather than rendering a checkbox the user
        // cannot tick.
        extensions: richTextExtensions({
            taskItem: ReadOnlyTaskItem.configure({ nested: true }),
            allowLinks: props.allowLinks,
        }),
        content: props.content || '',
    })

    useEffect(() => {
        editor?.commands.setContent(props.content || '')
    }, [editor, props.content])

    return <EditorContent editor={editor} className={mergeClasses(richTextStyles.content, props.className)} />
}
